/**
 * Swedish copy and pure helpers that belong to Att göra only (spec section 7).
 * Strings shared with other screens live in `src/ui/strings.ts`; everything
 * here is local to this folder so the page owns its own wording.
 */
import type { Ore } from '@/domain/money';
import { declarationDeadline, deadlineKind, formatSwedishDate } from '@/domain/periods/deadlines';
import { vatPeriodKeys } from '@/domain/periods/keys';
import { GENERIC_AI_ERROR, dispositionOf, pileSentences, type Disposition, type PileCounts } from '@/domain/inbox';
import { vatFromGross } from '@/domain/transactions';
import type { AssetChoice, Category } from '@/domain/catalog/types';
import type { AssetDecision } from '@/domain/catalog/rules';
import type {
  BankRow,
  Company,
  CompanySettings,
  FiscalYear,
  InboxFlag,
  InboxItem,
  InboxSource,
  IsoDate,
  Proposal,
  ReceiptReading,
  TransactionKind,
  Uuid,
  VatChoice,
} from '@/domain/types';
import { formatDateShort, formatSek, todayIso } from '../format';
import { BankIcon, CameraIcon, CloudIcon, PhoneIcon, UploadIcon, type IconComponent } from '../icons';

export const INBOX = {
  title: 'Att göra',
  refresh: (provider: string) => `Hämta nya kvitton från ${provider}`,
  refreshing: (provider: string) => `Läser Inkorgen i ${provider}…`,
  refreshDone: (found: number, provider: string) => (found === 0 ? `Inget nytt i Inkorgen i ${provider}.` : `Hämtade ${found} ${found === 1 ? 'fil' : 'filer'} från ${provider}.`),
  refreshFailed: (provider: string) => `Kunde inte läsa Inkorgen i ${provider}.`,
  refreshFailedNext: 'Kontrollera anslutningen under Mer › Lagring och försök igen.',
  uploadFailedNext: 'Försök igen, eller välj filen på nytt. Bilder och PDF:er upp till några megabyte fungerar bäst.',
  driveDisconnected: 'Ingen lagring är ansluten, så det finns ingen Inkorg att hämta från.',
  driveNeedsSignIn: 'Google-inloggningen har gått ut. Logga in igen för att hämta nya kvitton.',
  oneByOne: 'Gå igenom en i taget',
  reading: 'Läser kvitton',
  booking: 'Bokför',
  batchStopped: (booked: number) => `${booked} ${booked === 1 ? 'händelse bokfördes' : 'händelser bokfördes'} innan det tog stopp. Resten ligger kvar i Att göra.`,
  batchDone: (booked: number) => `${booked} ${booked === 1 ? 'händelse' : 'händelser'} bokförda.`,
} as const;

/**
 * Högen (spec section 7): what the user handed over, in their own words. The
 * sentences themselves come from the domain (`pileSentences`); everything here
 * is the wording around them.
 */
export const PILE = {
  /** The reconciliation runs after the reading and takes noticeably longer than nothing. */
  matching: 'Matchar mot kvitton och tidigare bokföring…',
  bankTitle: 'Stämmer banken?',
  batchOnly: (n: number) => `Visar bara det du just lade till (${n}).`,
  batchShowAll: 'Visa allt',
  batchDone: (rest: number) => `Allt du just lade till är hanterat. Det finns ${rest} ${rest === 1 ? 'sak' : 'saker'} till i Att göra.`,
  batchDoneLink: 'Visa resten',
  /** One answer for a whole group; never for anything outside it. */
  groupsTitle: 'Svara på flera samtidigt',
  groupAffected: 'Gäller:',
  groupAnswered: (n: number) => `Svaret gäller ${n} ${n === 1 ? 'händelse' : 'händelser'}.`,
  groupFailed: 'Alla svar kunde inte sparas. Öppna posterna som är kvar och svara på dem där.',
} as const;

export const TABS = {
  todo: 'Att göra',
  booked: 'Bokförda',
  skipped: 'Hoppade över',
} as const;

export type InboxTab = keyof typeof TABS;

/** Swedish value for the `?visa=` query parameter; the todo tab has none. */
export const TAB_PARAM: Record<InboxTab, string> = { todo: '', booked: 'bokforda', skipped: 'hoppade-over' };

export function tabFromParam(value: string | null): InboxTab {
  for (const [tab, param] of Object.entries(TAB_PARAM)) {
    if (param && param === value) return tab as InboxTab;
  }
  return 'todo';
}

export const REVIEW = {
  back: 'Att göra',
  book: 'Bokför',
  edit: 'Ändra',
  skip: 'Hoppa över',
  /**
   * [Hoppa över] asks for the reason, because the three reasons do different
   * things: a private receipt is filed as private, a duplicate says so in the
   * list, and "inte ett kvitto" is the one that means the file is not
   * bookkeeping at all.
   */
  skipTitle: 'Hoppa över',
  skipBody: 'Posten flyttas till fliken Hoppade över och filen flyttas till mappen Ej bokfört på Drive. Ingenting raderas – du kan lägga tillbaka den i Att göra när du vill.',
  skipWhy: 'Varför hoppar du över den?',
  skipNotReceipt: 'Inte ett kvitto',
  skipDuplicate: 'Dubblett – finns redan',
  skipPrivate: 'Privat – inte företagets',
  more: 'Fler val',
  privat: 'Privat – inte företagets',
  split: 'Dela upp i flera köp',
  invoice: 'Faktura – betalas senare',
  invoiceOn: 'Faktura – betalas senare (på)',
  notReceipt: 'Ta bort från Att göra (inte ett kvitto)',
  shortcuts: 'Kortkommandon',
  bookedToast: (ver: string) => `Bokfört som ${ver}`,
  undo: 'Ångra',
  undone: (ver: string) => `Rättelse bokförd som ${ver}. Bokföringen kan inte raderas, men vi bokför en rättelse.`,
  undoneMany: (vers: string) => `Rättelser bokförda som ${vers}. Bokföringen kan inte raderas, men vi bokför rättelser.`,
  undoFailed: 'Rättelsen kunde inte bokföras.',
  bookedTitle: (ver: string) => `Bokfört som ${ver}`,
  bookedBody: 'Händelsen ligger nu under Händelser. Kvittot arkiveras i Drive med verifikationsnumret i filnamnet.',
  /** A payment that settled two invoices books one händelse per invoice. */
  bookedBodyMany: 'Händelserna ligger nu under Händelser. Kvittot arkiveras i Drive med verifikationsnumret i filnamnet.',
  nextItem: 'Nästa',
  prevItem: 'Föregående',
  emptySelection: 'Välj en rad till vänster så visas den här.',
  missingItem: 'Posten finns inte längre i Att göra.',
  incomplete: 'Fyll i det som saknas innan du bokför.',
  missingTransferPair: 'Vi ser inte vilket av dina konton pengarna flyttades till. Importera kontoutdraget för det andra kontot, eller ändra vad det här är.',
  missingSettleTarget: 'Vi hittar ingen obetald faktura eller utlägg som betalningen hör till. Ändra vad det här är, eller lägg in fakturan först.',
  activationNote: (number: number, name: string) => `Kontot ${number} ${name} aktiveras i din kontoplan (loggas).`,
  splitTitle: 'Dela upp i flera köp',
  splitBody: 'Dela kvittot i delar med varsin kategori. Delarna måste tillsammans bli samma belopp som kvittot.',
  splitAdd: 'Lägg till en del',
  splitConfirm: 'Dela upp',
  beingRead: 'Kvittot läses just nu…',
  notReadYet: 'AI:n har inte läst kvittot än.',
  notReadYetNext: 'Fyll i uppgifterna själv, eller läs kvittot igen när AI:n är igång.',
  readFailedNext: 'Rätta felet och läs kvittot igen, eller fyll i uppgifterna själv.',
  readAgain: 'Läs igen',
  /** The bank row a receipt is paired with: confirmed reads as a fact, proposed as a suggestion. */
  matchConfirmed: (summary: string) => `Kopplad till banktransaktionen ${summary} ✓`,
  matchProposed: (summary: string) => `Föreslagen koppling: ${summary}`,
  matchMore: (n: number) => `och ${n} ${n === 1 ? 'transaktion' : 'transaktioner'} till`,
  matchConfirm: 'Stämmer',
  matchReject: 'Nej, annan betalning',
  matchSaveFailed: 'Kopplingen kunde inte sparas. Försök igen.',
  /** The duplicate flag's third answer: one purchase, two documents. */
  duplicateSamePurchase: 'Samma köp – lägg till som underlag',
  /** Which invoice a payment settles, or which purchase a credit note mirrors. */
  settleTargetTitle: 'Vilken faktura betalade du?',
  settleTargetEmpty: 'Det finns ingen obetald faktura eller utlägg att koppla betalningen till. Lägg in fakturan först, eller ändra vad det här är.',
  refundTargetTitle: 'Vilket köp gäller återbetalningen?',
  refundTargetEmpty: 'Vi hittar inget bokfört köp som återbetalningen kan höra till. Bokför köpet först, eller ändra vad det här är.',
  targetChange: 'Ändra',
  targetRemaining: (amount: string) => `${amount} kvar`,
} as const;

export const FLAG_TEXT = {
  duplicateKnown: (ver: string, date: string) => `Ser ut som samma köp som ${ver} den ${date}.`,
  duplicateUnknown: 'Ser ut som samma köp som något du redan har.',
  duplicateYes: 'Ja, dubblett – hoppa över',
  duplicateNo: 'Nej, annat köp',
  foreignCurrency: (currency: string, totalOre: Ore | null = null) =>
    totalOre !== null ? `Kvittot är på ${formatSek(totalOre, { unit: false })} ${currency}. Vad drogs från kontot i kronor?` : `Kvittot är i ${currency}. Vad drogs från kontot i kronor?`,
  foreignCurrencyAction: 'Skriv beloppet i kronor',
  possiblePrivate: 'Det här kan vara ett privat köp. Kolla att det är företagets.',
  possiblePrivateYes: 'Ja, privat',
  possiblePrivateNo: 'Nej, företagets köp',
  lockedPeriod: (firstOpen: string) => `Perioden är låst. Bokförs på första öppna dag: ${firstOpen}`,
  lockedPeriodAction: (firstOpen: string) => `Använd ${firstOpen}`,
  noVatBreakdown: 'Kvittot visar ingen momsrad. Vilken moms gäller?',
  noVatBreakdownAction: 'Välj momssats',
  unreadable: 'Kvittot går inte att läsa. Fyll i uppgifterna själv eller ta en ny bild.',
  representation: 'Representation. Bara en del av momsen får dras av – ange antal personer.',
  foreignSupplier: 'Utländsk säljare. Omvänd moms kan gälla.',
  outsideFiscalYear: 'Datumet ligger utanför ett öppet räkenskapsår. Ändra datumet eller öppna räkenskapsåret först.',
  largeAmount: 'Stort belopp. Kolla en extra gång innan du bokför.',
} as const;

export const SHORTCUTS: readonly { keys: string; what: string }[] = [
  { keys: 'J', what: 'Nästa post' },
  { keys: 'K', what: 'Föregående post' },
  { keys: 'Enter', what: 'Bokför' },
  { keys: 'E', what: 'Ändra det som är osäkert' },
  { keys: 'P', what: 'Privat – inte företagets' },
  { keys: 'S', what: 'Hoppa över' },
  { keys: '?', what: 'Visa den här listan' },
];

export const SOURCE_LABEL: Record<InboxSource, string> = {
  upload: 'Uppladdad fil',
  phone: 'Fotad med mobilen',
  share: 'Delad från en annan app',
  drive: 'Hämtad från Drive',
  picker: 'Vald i Google Drive',
  bank: 'Rad från kontoutdraget',
};

export function sourceIcon(source: InboxSource): IconComponent {
  switch (source) {
    case 'upload':
      return UploadIcon;
    case 'phone':
      return CameraIcon;
    case 'share':
      return PhoneIcon;
    case 'drive':
    case 'picker':
      return CloudIcon;
    case 'bank':
      return BankIcon;
    default: {
      const exhaustive: never = source;
      throw new Error(`Unknown inbox source ${String(exhaustive)}`);
    }
  }
}

/** "hos" for purchases, "till" for sales; the sentence reads as prose either way. */
export function counterpartyPreposition(kind: TransactionKind): string {
  switch (kind) {
    case 'sale':
    case 'settle':
      return 'till';
    case 'transfer':
      return 'mellan';
    case 'owner_in':
    case 'owner_out':
    case 'tax':
    case 'vat_settlement':
    case 'year_end':
    case 'other':
      return 'med';
    case 'purchase':
    case 'refund':
      return 'hos';
    default: {
      const exhaustive: never = kind;
      throw new Error(`Unknown transaction kind ${String(exhaustive)}`);
    }
  }
}

/** The chips a proposal can open, in the order the sentence reads. */
export type ChipField = 'kind' | 'counterparty' | 'date' | 'amount' | 'category' | 'payment' | 'vat';

export type ChipState = 'normal' | 'uncertain' | 'missing';

/** Fields the reading flagged as uncertain, normalised to chip names. */
export function uncertainChips(reading: ReceiptReading | undefined): ReadonlySet<ChipField> {
  const out = new Set<ChipField>();
  for (const raw of reading?.uncertainFields ?? []) {
    const field = raw.toLowerCase();
    if (field.includes('merchant') || field.includes('leverant')) out.add('counterparty');
    if (field.includes('date') || field.includes('datum')) out.add('date');
    if (field.includes('total') || field.includes('amount') || field.includes('belopp')) out.add('amount');
    if (field.includes('vat') || field.includes('moms')) out.add('vat');
    if (field.includes('categ') || field.includes('kategori')) out.add('category');
    if (field.includes('payment') || field.includes('betal')) out.add('payment');
  }
  return out;
}

/** The first field worth editing (the E shortcut): missing beats uncertain, in sentence order. */
export function firstUncertainChip(proposal: Proposal, reading: ReceiptReading | undefined, needsCategory: boolean): ChipField {
  const uncertain = uncertainChips(reading);
  const order: ChipField[] = ['amount', 'date', 'category', 'counterparty', 'vat', 'payment'];
  const missing = order.find((field) => {
    switch (field) {
      case 'amount':
        return proposal.amountOre === null || proposal.amountOre <= 0;
      case 'date':
        return proposal.date === null;
      case 'category':
        return needsCategory && !proposal.categoryId;
      case 'counterparty':
        return !proposal.counterparty.trim();
      case 'vat':
        return proposal.vat === null;
      case 'payment':
        return !proposal.paymentSourceId;
      case 'kind':
        return false;
      default: {
        const exhaustive: never = field;
        throw new Error(`Unknown chip ${String(exhaustive)}`);
      }
    }
  });
  if (missing) return missing;
  const question = proposal.question?.field;
  if (question === 'category' || question === 'vat' || question === 'payment' || question === 'kind') return question;
  return order.find((field) => uncertain.has(field)) ?? 'category';
}

/** Tone of the notice that stands in for the confidence badge. */
export type PrefillNoticeTone = 'info' | 'warning' | 'danger';

export interface PrefillNotice {
  tone: PrefillNoticeTone;
  message: string;
  /** The next step, shown after the message. */
  next?: string;
  /** The provider's own wording, for "Tekniska detaljer". */
  technical?: string;
}

/**
 * What to show where the confidence badge goes. An item without a proposal has
 * no confidence to report: falling back to 0 makes the badge read "AI-förslag,
 * osäker" for a receipt the AI never read, which hides the real reason (a model
 * error, no key, or an item still in the queue). Returns null when the badge is
 * the right thing to show.
 */
export function prefillNotice(item: Pick<InboxItem, 'status' | 'error' | 'errorTechnical' | 'proposal'>): PrefillNotice | null {
  if (item.status === 'processing') return { tone: 'info', message: REVIEW.beingRead };
  if (item.status === 'error') {
    return {
      tone: 'danger',
      message: item.error ?? GENERIC_AI_ERROR,
      next: REVIEW.readFailedNext,
      ...(item.errorTechnical ? { technical: item.errorTechnical } : {}),
    };
  }
  if (item.proposal) return null;
  if (item.error) return { tone: 'warning', message: item.error };
  return { tone: 'warning', message: REVIEW.notReadYet, next: REVIEW.notReadYetNext };
}

/**
 * The flags the review card shows. A flag the user has answered is hidden, and
 * `outside_fiscal_year` is re-derived from the date the card would book rather
 * than the one the item was triaged on: it has to go the moment the date is
 * fixed, and appear when an edit moves the date out of every open year. It is
 * the only place that sentence may be shown, so the same problem never renders
 * twice (the compiler says it too, see `compileItem`).
 */
/**
 * The inventarie line (spec section 7): shown on every purchase whose category
 * can be an inventarie, whichever way the amount went, so the rule is never a
 * surprise. One sentence states the decision and its reason; the one button
 * is the other answer. Overridden, the sentence says whose decision it is and
 * drops the reason — the amount no longer decided anything.
 */
export const ASSET_LINE = {
  asset: (threshold: Ore) => `Bokförs som inventarie och skrivs av – beloppet är minst ett halvt prisbasbelopp (${formatSek(threshold, { trimZeroOre: true })}).`,
  expense: (threshold: Ore) => `Bokförs som kostnad – beloppet är under ett halvt prisbasbelopp (${formatSek(threshold, { trimZeroOre: true })}).`,
  chosenAsset: 'Du valde: bokförs som inventarie och skrivs av.',
  chosenExpense: 'Du valde: bokförs som kostnad.',
  toAsset: 'Bokför som inventarie',
  toExpense: 'Bokför som kostnad',
} as const;

export function assetLineText(decision: AssetDecision): string {
  if (decision.overridden) return decision.books === 'asset' ? ASSET_LINE.chosenAsset : ASSET_LINE.chosenExpense;
  return decision.books === 'asset' ? ASSET_LINE.asset(decision.thresholdOre) : ASSET_LINE.expense(decision.thresholdOre);
}

/** The button on the line: the answer the draft is not currently giving. */
export function assetLineAction(decision: AssetDecision): { label: string; choice: AssetChoice } {
  return decision.books === 'asset' ? { label: ASSET_LINE.toExpense, choice: 'expense' } : { label: ASSET_LINE.toAsset, choice: 'asset' };
}

export function visibleFlags(flags: readonly InboxFlag[], isHidden: (flag: InboxFlag) => boolean, dateOutsideYear: boolean): InboxFlag[] {
  const kept = flags.filter((flag) => flag !== 'outside_fiscal_year' && !isHidden(flag));
  return dateOutsideYear ? ['outside_fiscal_year', ...kept] : kept;
}

/** Whether a kind books on a category at all (purchase/sale/refund do). */
export function kindNeedsCategory(kind: TransactionKind): boolean {
  return kind === 'purchase' || kind === 'sale' || kind === 'refund';
}

/** The VAT amount to print in the chip: the receipt's own figure when known. */
export function vatAmountOf(vat: VatChoice | null, amountOre: Ore | null): Ore | null {
  if (!vat || amountOre === null || amountOre <= 0) return null;
  if (vat.vatOre !== undefined) return vat.vatOre;
  if (vat.mode !== 'domestic' || vat.rate === 0) return null;
  return vatFromGross(amountOre, vat.rate);
}

/** "Köp hos Clas Ohlson · Kontorsmaterial" for batch lines and row titles. */
export function itemSentence(item: InboxItem, catalog: readonly Category[], kindWord: string): string {
  const p = item.proposal;
  const who = (p?.counterparty ?? item.reading?.merchant ?? '').trim();
  const category = p?.categoryId ? catalog.find((c) => c.id === p.categoryId)?.label : undefined;
  const head = who ? `${kindWord} ${counterpartyPreposition(p?.kind ?? 'purchase')} ${who}` : kindWord;
  return category ? `${head} · ${category}` : head;
}

/**
 * Why the card cannot book yet, in the user's words. A transfer needs the
 * account the money went to and a settlement the invoice it pays; neither can
 * be picked in the sentence, so the card says what is missing instead of
 * repeating the generic "fyll i det som saknas".
 */
export function incompleteText(kind: TransactionKind, extras: { counterpartySourceId?: Uuid | undefined; settlesVerifikationId?: Uuid | undefined }): string {
  if (kind === 'transfer' && !extras.counterpartySourceId) return REVIEW.missingTransferPair;
  if (kind === 'settle' && !extras.settlesVerifikationId) return REVIEW.missingSettleTarget;
  return REVIEW.incomplete;
}

/**
 * "Nästa moms ska in 12 november." — the first declaration deadline that has
 * not passed, in the company's own VAT period. Null when the company is not
 * VAT registered, has no open year, or files together with the income tax
 * return (no exact date to promise).
 */
export function nextVatDeadlineSentence(
  company: Company | null | undefined,
  settings: CompanySettings | undefined,
  fiscalYears: readonly FiscalYear[],
  today = todayIso(),
): string | null {
  if (!company || !settings?.vatRegistered) return null;
  if (deadlineKind(settings.vatPeriod, company.entityType) === 'with_income_tax_return') return null;
  const years = [...fiscalYears].filter((fy) => fy.status === 'open').sort((a, b) => a.startDate.localeCompare(b.startDate));
  for (const fy of years) {
    for (const period of vatPeriodKeys(fy, settings.vatPeriod, company.entityType)) {
      const deadline = declarationDeadline(period.to, settings.vatPeriod, company.entityType);
      if (deadline >= today) return `Nästa moms ska in ${formatSwedishDate(deadline, { relativeTo: today })}.`;
    }
  }
  return null;
}

/**
 * Title for a row: the merchant, else the bank row's own text, else the note,
 * else the source. A bank row that has not been read yet has no proposal and
 * no merchant, and "Rad från kontoutdraget" tells the user nothing — its text
 * from the statement does.
 */
export function itemTitle(item: InboxItem, bankRow?: Pick<BankRow, 'text'> | undefined): string {
  const merchant = (item.proposal?.counterparty ?? item.reading?.merchant ?? '').trim();
  if (merchant) return merchant;
  const bankText = bankRow?.text?.trim();
  if (bankText) return bankText;
  const note = item.note?.trim();
  if (note) return note;
  return SOURCE_LABEL[item.source];
}

// ---- The pile ---------------------------------------------------------------

/** "A12", "A12 och A13", "A12, A13 och A14" — one booking or several. */
export function listSwedish(parts: readonly string[]): string {
  if (parts.length <= 1) return parts[0] ?? '';
  return `${parts.slice(0, -1).join(', ')} och ${parts[parts.length - 1]}`;
}

/**
 * Only the counts that wait on something that has not arrived — an invoice's
 * payment, a payment's statement row, a purchase's receipt. The empty screen
 * says these out loud so it never claims that everything is booked while the
 * bookkeeping is still owed something.
 */
export function pendingSentences(counts: PileCounts): string[] {
  return pileSentences({
    prepared: 0,
    questions: 0,
    missingEvidence: 0,
    failed: 0,
    waiting: 0,
    resolved: 0,
    pendingSettlements: counts.pendingSettlements,
    pendingInvoices: counts.pendingInvoices,
    missingReceipts: counts.missingReceipts,
  });
}

/** The empty screen's body: the honest "done" sentence, then the next VAT deadline. */
export function allDoneBody(body: string, deadline: string | null): string {
  return [body, deadline].filter((s): s is string => !!s && s.length > 0).join(' ');
}

export const DONE_FALLBACK_BODY = 'Nya kvitton dyker upp här så fort de kommer in.';

/** The Swedish word for how a finished item ended, for the Bokförda/Hoppade över lists. */
export const DISPOSITION_TEXT: Record<Disposition, string> = {
  booked: 'Bokförd',
  attached: 'Kopplad till händelse',
  duplicate: 'Dubblett',
  private: 'Privat',
  not_receipt: 'Inte ett kvitto',
  merged: 'Underlag till annat kvitto',
  split: 'Delad',
  skipped: 'Hoppad över',
};

/** The reason the item was given, or — when it has none — the word for how it ended. */
export function dispositionSubtitle(item: InboxItem): string | undefined {
  const reason = item.tierReasons[0]?.trim();
  if (reason) return reason;
  const disposition = dispositionOf(item);
  return disposition ? DISPOSITION_TEXT[disposition] : undefined;
}

// ---- Grouped questions -------------------------------------------------------

/** What the items of a question group are, so the heading can name them. */
export function groupNoun(items: readonly Pick<InboxItem, 'kind'>[]): string {
  if (items.length && items.every((i) => i.kind === 'receipt')) return items.length === 1 ? 'kvitto' : 'kvitton';
  if (items.length && items.every((i) => i.kind === 'bank_row')) return items.length === 1 ? 'bankrad' : 'bankrader';
  return items.length === 1 ? 'händelse' : 'händelser';
}

/** "Samma fråga för 3 kvitton från Spotify". */
export function questionGroupHeading(items: readonly Pick<InboxItem, 'kind'>[], subject: string): string {
  const head = `Samma fråga för ${items.length} ${groupNoun(items)}`;
  return subject.trim() ? `${head} från ${subject.trim()}` : head;
}

// ---- The bank row a receipt is paired with ------------------------------------

/** "6 mars · 625,00 kr · K*CLAS OHLSON" — the row as one line, sign dropped. */
export function bankRowSummary(row: Pick<BankRow, 'bookingDate' | 'amountOre' | 'text'>, today: IsoDate = todayIso()): string {
  const parts = [formatDateShort(row.bookingDate, today), formatSek(Math.abs(row.amountOre))];
  const text = row.text.trim();
  if (text) parts.push(text);
  return parts.join(' · ');
}

// ---- Settlements and refunds --------------------------------------------------

/**
 * What a settlement or a refund is aimed at, once the user has picked it:
 * "Betalar fakturan A7 · 1 250,00 kr", "Återbetalar köpen A7 och A9 · 300,00 kr".
 */
/**
 * How much of a payment goes on one target: never more than the target still
 * owes, never more than the payment itself. The booking checks this again
 * inside its transaction, on the figures as they are then.
 */
export function allocationAmount(amountOre: Ore | null, remainingOre: Ore): Ore {
  if (amountOre === null || amountOre <= 0) return remainingOre;
  return Math.min(amountOre, remainingOre);
}

export function allocationSentence(kind: 'settle' | 'refund', labels: readonly string[], totalOre: Ore | null): string {
  if (!labels.length) return '';
  const many = labels.length > 1;
  const head = kind === 'refund' ? (many ? 'Återbetalar köpen' : 'Återbetalar köpet') : many ? 'Betalar fakturorna' : 'Betalar fakturan';
  const sentence = `${head} ${listSwedish(labels)}`;
  return totalOre !== null && totalOre > 0 ? `${sentence} · ${formatSek(totalOre)}` : sentence;
}

// ---- The bank import's own summary ---------------------------------------------

export interface ImportSummaryCounts {
  /** Rows the reconciliation prepared for approval in Att göra. */
  prepared: number;
  /** Rows a receipt or an earlier booking already explains. */
  linked: number;
  /** Rows that still need an answer. */
  questions: number;
}

/** "28 rader är förberedda för godkännande · 9 kopplades till kvitton eller tidigare händelser · 3 frågor kvar". */
export function importSummarySentence(counts: ImportSummaryCounts): string {
  const parts: string[] = [];
  parts.push(`${counts.prepared} ${counts.prepared === 1 ? 'rad är förberedd' : 'rader är förberedda'} för godkännande`);
  if (counts.linked) parts.push(`${counts.linked} ${counts.linked === 1 ? 'kopplades' : 'kopplades'} till kvitton eller tidigare händelser`);
  parts.push(`${counts.questions} ${counts.questions === 1 ? 'fråga kvar' : 'frågor kvar'}`);
  return parts.join(' · ');
}

export const BANK_IMPORT = {
  preparing: 'Matchar raderna mot kvitton och tidigare bokföring…',
  prepareFailed: 'Bankraderna kunde inte förberedas.',
  prepareFailedNext: 'Ladda om sidan – raderna är importerade och finns kvar.',
  approve: 'Godkänn de förberedda i Att göra',
  home: 'Till Hem',
  walkTitle: 'Vad var det här?',
  allAnswered: 'Alla rader är genomgångna',
  /** Nothing more is asked here, but something is still waiting in Att göra. */
  doneForNow: 'Klart för den här gången',
} as const;
